import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  Typography,
  DialogContent,
  IconButton,
  Button,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import { emailRegex } from 'utils/stringUtils';
import { useDispatch, useSelector } from 'react-redux';
import { sendMail } from 'containers/Main/actions';
import { toast } from 'react-toastify';
import Mixpanel from 'utils/mixpanelService';
import { getSupportTypeName } from '../../utils';
import CloseIcon from '../../icons/CloseIcon.svg';

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiBackdrop-root': {
      backdropFilter: 'blur(4px)',
    },
  },
  button: {
    borderRadius: 8,
    marginTop: 24,
    width: '100%',
    padding: 14,
    ...theme.typography.button,
  },
  type: {
    marginTop: 32,
    '& [role="button"]:focus-visible': {
      outline: 'none !important',
    },
  },
  menuItem: {
    '&:focus-visible': {
      outline: 'none !important',
    },
  },
  dialogPaper: {
    position: 'absolute',
    minWidth: 620,
    borderRadius: '8px !important',
    [theme.breakpoints.down('800')]: {
      minWidth: 'initial',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    padding: 45,
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1,
    padding: 25,
  },
  placeholder: {
    color: '#aaa',
  },
  form: { marginTop: 8 },
  label: { transform: 'translate(0, -4px) scale(0.75)' },
}));

const SupportModal = ({
  onClose,
  widgetCopy,
  getEmailData,
  getMixpanelData,
  type = 'support',
  initializeFromProfile = true,
  onSubmit,
  isLoading,
}) => {
  const classes = useStyles();
  const [formValues, setFormValues] = useState({
    requestType: null,
    name: '',
    email: '',
    message: '',
  });

  const requestTypes =
    widgetCopy?.types?.map(el => ({
      value: el,
      label: el,
    })) || [];

  const dispatch = useDispatch();
  const profile = useSelector(state => state.firebase.profile);
  const processing = useSelector(state => state.main.sendMailProcessing);
  const sendMailStatus = useSelector(state => state.main.mailSent);
  const clientDetails = useSelector(state => state.main.clientDetails);
  const [emailError, setEmailError] = useState('');
  const brand = clientDetails?.formalShortName;
  const disabled = Object.values(formValues).some(el => !el) || !!emailError;
  const hasSent = useRef(false);

  useEffect(() => {
    if (initializeFromProfile && profile.isLoaded && !profile.isEmpty) {
      setFormValues(prev => ({
        ...prev,
        name: profile?.displayName,
        email: profile?.email,
      }));
    }
  }, [initializeFromProfile, profile]);

  const handleSubmitSuccess = () => {
    const message = widgetCopy?.message;
    toast(message, { type: 'success' });

    // Track mixpanel event
    const supportTypeName = getSupportTypeName(type);
    const trackingData = getMixpanelData?.(formValues) || {};
    Mixpanel.track(`${supportTypeName} - Need Help - Submitted`, trackingData);

    onClose();
  };

  useEffect(() => {
    if (hasSent.current && sendMailStatus) {
      handleSubmitSuccess();
    }
  }, [sendMailStatus]);

  const handleChange = ({ target }) => {
    if (!target?.name) return;

    setFormValues(prev => ({ ...prev, [target.name]: target.value }));

    if (target.name === 'email') {
      setEmailError(
        emailRegex.test(target.value) ? '' : widgetCopy?.emailError,
      );
    }
  };

  const handleSelect = currentValue => {
    setFormValues({ ...formValues, requestType: currentValue });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!emailError) {
      // Transform data using getEmailData
      const emailData = getEmailData?.(formValues) || {};

      const dataToSubmit = {
        ...emailData,
        type,
        brand,
      };

      if (onSubmit) {
        await onSubmit(dataToSubmit, {
          onSuccess: () => {
            handleSubmitSuccess();
          },
        });

        return;
      }

      hasSent.current = true;
      // By default, send email
      dispatch(sendMail(dataToSubmit));
    }
  };

  const isProcessing = isLoading || processing;

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper, root: classes.root }}
      open
      onClose={onClose}
    >
      <IconButton
        onClick={onClose}
        className={classes.closeButton}
        aria-label="Close Menu"
      >
        <img src={CloseIcon} alt="" />
      </IconButton>
      <DialogContent className={classes.content}>
        <Typography color="primary" variant="h2">
          {widgetCopy?.title}
        </Typography>
        <Typography variant="body2">{widgetCopy?.description}</Typography>
        <form
          className={classes.form}
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="name"
                name="name"
                placeholder={widgetCopy?.namePlaceholder}
                onChange={handleChange}
                value={formValues.name}
                fullWidth
                variant="standard"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                id="email"
                name="email"
                type="email"
                placeholder={widgetCopy?.emailPlaceholder}
                onChange={handleChange}
                value={formValues.email}
                autoComplete="email"
                fullWidth
                variant="standard"
                error={!!emailError}
                helperText={emailError}
              />
            </Grid>
            <Grid item xs={12} className={classes.type}>
              <TextField
                id="requestType"
                name="requestType"
                label={widgetCopy?.requestPlaceholder}
                select
                fullWidth
                value={formValues.requestType}
                variant="standard"
                InputLabelProps={{
                  shrink: true,
                  classes: { root: classes.label },
                }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: selected =>
                    selected ? (
                      requestTypes.find(el => el.value === selected)?.label
                    ) : (
                      <span className={classes.placeholder}>
                        {widgetCopy?.selectOption}
                      </span>
                    ),
                }}
              >
                {requestTypes.map(({ value: currentValue, label }) => (
                  <MenuItem
                    key={currentValue}
                    value={currentValue}
                    onClick={() => handleSelect(currentValue)}
                    classes={{ root: classes.menuItem }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="message"
                name="message"
                multiline
                fullWidth
                minRows={5}
                placeholder={widgetCopy?.yourMessage}
                value={formValues.message}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Button
            variant="contained"
            color="primary"
            size="large"
            disabled={disabled || isProcessing}
            type="submit"
            className={classes.button}
          >
            {isProcessing ? <CircularProgress size={17} /> : widgetCopy?.submit}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SupportModal;
